import styled, { CSSObject } from "@emotion/styled";
import { m, useMotionValue, useTransform, useScroll } from "framer-motion";
import React, {
  forwardRef,
  PropsWithChildren,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { defaultTheme } from "@brexhq/shared/styles/theming";

import { useMergeRefs } from "../../hooks/useMergeRefs";
import { BreakPointVals } from "../../styles/breakpoints";
import { SanityTextRowProps } from "../../types/modules/BlockRowsModule/BlockRowItems";

import styles, { fontWeightMap } from "./styles";
import { BaseTextProps } from "./types";
import { generateMargins, filterColorProp } from "./utils";

export type TextComponentsObj = {
  LRG: TextComponents;
  MED: TextComponents;
  SML: TextComponents;
};

export type TextComponents = keyof typeof styles;

type ResponsiveArgs = {
  [Key in BreakPointVals]?: CSSObject;
};

export type FontWeightOverride = "BOLD" | "TITLE" | "BODY" | "REGULAR";

interface TextComponentProps extends BaseTextProps {
  components?: TextComponentsObj;
  children: React.ReactNode;
  textAnimation?: SanityTextRowProps["textAnimation"] | null;
  fontFamily?: string | null;
  noWrap?: boolean;
}

export const responsiveRules = (
  responsiveGroups: Array<ResponsiveArgs>,
): CSSObject => {
  const values = {} as ResponsiveArgs;
  for (let rule = 0; rule < responsiveGroups.length; rule++) {
    for (const property in responsiveGroups[rule]) {
      const currentStyles = responsiveGroups[rule];
      values[property] = { ...values[property], ...currentStyles[property] };
    }
  }
  const {
    breakpoints: { MED, SML, LRG },
  } = defaultTheme;

  /// Combining responsive objects that are passed to this function
  // if you have multiple media queries they get overwritten

  return {
    ...(values?.LRG && values.LRG),
    [LRG]: {
      ...(values?.LRG && values.LRG),
    },
    [MED]: {
      ...(values?.MED && values.MED),
    },
    [SML]: {
      ...(values?.SML && values.SML),
    },
  };
};

// Sanitizes string children
const SanitizedText: React.FC<PropsWithChildren> = ({ children }) => {
  return (
    <>
      {React.Children.map(children, (child) => {
        if (typeof child === "string") {
          // Remove L-SEP
          return child.replace(/\u2028/g, "");
        }

        return child;
      })}
    </>
  );
};

const StyledText = styled<any>(
  "p",
  filterColorProp,
)<TextComponentProps>(
  ({
    color,
    theme,
    fontFamily,
    components,
    gradient,
    margin,
    fontWeightOverride,
    noWrap,
  }) => {
    const generatedMargins = generateMargins(margin);
    if (!components) {
      return {};
    }
    const responsiveTextObject = Object.keys(components).reduce(
      (obj, value) => {
        return {
          ...obj,
          [value]: styles[components[value]],
        };
      },
      {},
    );

    const gradientStyles = {
      background: gradient,
      backgroundClip: "text",
      textFillColor: "transparent",
    };

    if (fontFamily === "Inter" || !fontFamily) {
      fontFamily = "var(--font-inter)";
    }

    const fontSizeMap = {
      DisplayL: "clamp(160px, calc(7.5325rem + 3.8554vw), 176px)",
      DisplayM: "clamp(128px, calc(5.5325rem + 3.8554vw), 144px)",
      DisplayS: "clamp(104px, calc(5.2663rem + 1.9277vw), 112px)",
    };

    const letterSpacingMap = {
      DisplayL: "clamp(-8.8px, calc(-0.3766rem + -0.1928vw), -8px)",
      DisplayM: "clamp(-7.2px, calc(-0.2766rem + -0.1928vw), -6.4px)",
      DisplayS: "clamp(-5.6px, calc(-0.2633rem + -0.0964vw), -5.2px)",
      TitleL: "-2.88px",
      TitleM: "-1.44px",
      TitleS: "-0.72px",
      SubtitleL: "-0.6px",
      SubtitleM: "-0.48px",
      SubtitleS: "-0.4px",
    };

    const lineHeightMap = {
      TitleL: "70.56px",
      TitleM: "48px",
      TitleS: "36px",
      SubtitleL: "33px",
      SubtitleM: "26.4px",
      SubtitleS: "22px",
    };

    const fontOverride =
      fontWeightOverride === "BODY" || fontWeightOverride === "REGULAR"
        ? Object.entries(components).reduce((acc, [breakpoint, component]) => {
            const hasFontSize =
              fontSizeMap[component as keyof typeof fontSizeMap];
            const hasLetterSpacing =
              letterSpacingMap[component as keyof typeof letterSpacingMap];
            const hasLineHeight =
              lineHeightMap[component as keyof typeof lineHeightMap];

            if (hasFontSize || hasLetterSpacing || hasLineHeight) {
              acc[breakpoint] = {
                ...(hasLetterSpacing && {
                  letterSpacing: `${hasLetterSpacing} !important`,
                }),
                ...(hasLineHeight && {
                  lineHeight: `${hasLineHeight} !important`,
                }),
                ...(hasFontSize && { fontSize: `${hasFontSize} !important` }),
              };
            }
            return acc;
          }, {} as Record<string, any>)
        : {};

    return {
      fontFamily: `${
        fontFamily.startsWith("var") ? `${fontFamily}` : `"${fontFamily}"`
      }, sans-serif`,
      // FIXME: This component is used in Sanity via generateBlockSchema, but Sanity does not have theme.colors.content fallback.
      // We should stop importing this component into cms, at which point we can remove the defaultTheme.colors.content fallback.
      color:
        color ?? theme.colors?.content.primary ?? defaultTheme.colors.content,
      margin: 0,
      transition: "opacity .25s ease-in -out",
      ...(gradient ? gradientStyles : {}),
      ...responsiveRules([
        responsiveTextObject,
        generatedMargins,
        fontWeightOverride != null ? fontOverride : {},
      ]),
      fontVariationSettings: fontWeightOverride
        ? `${fontWeightMap[fontWeightOverride]} !important`
        : undefined,
      whiteSpace: noWrap ? "nowrap" : "pre-wrap",
      a: {
        textDecoration: "underline",
        color: "inherit",
        "&:hover": {
          opacity: 0.8,
          color: "inherit",
        },
      },
      "& > a": {
        textDecoration: "underline",
        color: "inherit",
        "&:hover": {
          opacity: 0.8,
          color: "inherit",
        },
      },
      ul: {
        ...responsiveRules([responsiveTextObject, generatedMargins]),
      },
      ol: {
        ...responsiveRules([responsiveTextObject, generatedMargins]),
      },
    };
  },
);

/**
 * Core low level component used to implement all other typography styles
 */
export const Text = forwardRef<HTMLElement, TextComponentProps>(function Text(
  { children, textAnimation, ...props },
  ref,
) {
  if (textAnimation?.enabled && Boolean(textAnimation?.color?.rgb)) {
    return (
      <AnimatedText {...props} textAnimation={textAnimation} ref={ref}>
        {children}
      </AnimatedText>
    );
  }

  return (
    <StyledText {...props} ref={ref}>
      <SanitizedText>{children}</SanitizedText>
    </StyledText>
  );
});

const MotionComponent = m.create(StyledText);

const AnimatedText = forwardRef<HTMLElement, TextComponentProps>(
  function AnimatedText({ children, textAnimation, ...props }, ref) {
    const mouseX = useMotionValue(50);
    const mouseY = useMotionValue(50);
    const textRef = useRef<HTMLParagraphElement>(null);
    const { scrollYProgress } = useScroll({
      target: textRef,
    });
    const [isMobile, setIsMobile] = useState(false);
    const mergedRefs = useMergeRefs([ref, textRef]);
    const color = textAnimation?.color?.rgb
      ? `rgba(${textAnimation.color.rgb.r}, ${textAnimation.color.rgb.g}, ${textAnimation.color.rgb.b}, ${textAnimation.color.rgb.a})`
      : "rgba(255, 89, 0, 1)";

    const background = useTransform(
      [mouseX, mouseY, scrollYProgress],
      ([x, y, scroll]) => {
        if (isMobile) {
          // On mobile, use scroll position for both X and Y
          return `radial-gradient(circle at ${
            (scroll as number) * 500
          }% 50%, white 0%, ${color} 100%)`;
        }
        // On desktop, use mouse position
        return `radial-gradient(circle at ${x}% ${y}%, white 0%, ${color} 100%)`;
      },
    );

    const handleMouseMove = useCallback(
      (e: MouseEvent) => {
        if (!isMobile) {
          const x = (e.clientX / window.innerWidth) * 100;
          const y = (e.clientY / window.innerHeight) * 100;
          mouseX.set(x);
          mouseY.set(y);
        }
      },
      [isMobile, mouseX, mouseY],
    );

    const checkMobile = useCallback(() => {
      setIsMobile(window.innerWidth <= 768);
    }, []);

    useEffect(() => {
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("resize", checkMobile);

      return () => {
        window.removeEventListener("mousemove", handleMouseMove);
        window.removeEventListener("resize", checkMobile);
      };
    }, [handleMouseMove, checkMobile]);

    return (
      <MotionComponent
        style={{
          backgroundImage: background,
          WebkitBackgroundClip: "text",
          backgroundClip: "text",
          WebkitTextFillColor: "transparent",
          backgroundSize: "100%",
        }}
        ref={mergedRefs}
        {...props}
      >
        <SanitizedText>{children}</SanitizedText>
      </MotionComponent>
    );
  },
);
